/**
 * Image compression utility functions
 */

export interface ImageCompressionOptions {
  maxWidth?: number
  maxHeight?: number
  quality?: number
}

export interface UploadOptions extends ImageCompressionOptions {
  skipCompression?: boolean
  onProgress?: (stats: CompressionStats) => void
}

export interface CompressionStats {
  originalSizeMB: string
  compressedSizeMB: string
  compressionRatio: string
  message: string
}

/**
 * Compresses an image file using HTML5 Canvas API
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise that resolves to the compressed file
 */
export const compressImage = (
  file: File,
  options: ImageCompressionOptions = {}
): Promise<File> => {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8
  } = options

  return new Promise((resolve, reject) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      reject(new Error('File is not an image'))
      return
    }

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width
            width = maxWidth
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height
            height = maxHeight
          }
        }

        // Set canvas dimensions
        canvas.width = width
        canvas.height = height

        if (!ctx) {
          reject(new Error('Could not get canvas context'))
          return
        }

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height)
        
        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not compress image'))
              return
            }

            // Create new file with compressed data
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            })

            resolve(compressedFile)
          },
          file.type,
          quality
        )
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => reject(new Error('Could not load image'))
    
    // Create object URL and clean up when done
    const objectUrl = URL.createObjectURL(file)
    img.src = objectUrl
    
    // Clean up object URL after image loads or fails
    img.onload = (originalOnLoad => () => {
      URL.revokeObjectURL(objectUrl)
      originalOnLoad?.()
    })(img.onload)
    
    img.onerror = (originalOnError => () => {
      URL.revokeObjectURL(objectUrl)
      originalOnError?.()
    })(img.onerror)
  })
}

/**
 * Get compression stats for logging
 * @param originalFile - Original file
 * @param compressedFile - Compressed file
 * @returns Compression statistics
 */
export const getCompressionStats = (originalFile: File, compressedFile: File): CompressionStats => {
  const originalSizeMB = (originalFile.size / 1024 / 1024).toFixed(2)
  const compressedSizeMB = (compressedFile.size / 1024 / 1024).toFixed(2)
  const compressionRatio = ((originalFile.size - compressedFile.size) / originalFile.size * 100).toFixed(1)
  
  return {
    originalSizeMB,
    compressedSizeMB,
    compressionRatio,
    message: `Image compressed: ${originalSizeMB}MB → ${compressedSizeMB}MB (${compressionRatio}% reduction)`
  }
}

/**
 * Uploads a file with optional compression
 * @param file - File to upload
 * @param api - Repository API instance
 * @param options - Upload and compression options
 * @returns Promise that resolves to the upload response
 */
export const uploadFileCompressed = async (
  file: File,
  api: any, // Using any to avoid complex API typing
  options: UploadOptions = {}
) => {
  const {
    skipCompression = false,
    onProgress,
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8
  } = options

  let fileToUpload = file

  // Compress image if it's an image file and compression is not skipped
  if (file.type.startsWith('image/') && !skipCompression) {
    try {
      const compressedFile = await compressImage(file, {
        maxWidth,
        maxHeight,
        quality
      })

      const stats = getCompressionStats(file, compressedFile)
      
      // Call progress callback if provided
      if (onProgress) {
        onProgress(stats)
      } else {
        // Default logging
        console.log(stats.message)
      }

      fileToUpload = compressedFile
    } catch (error) {
      console.warn('Image compression failed, uploading original file:', error)
      // Continue with original file if compression fails
    }
  }

  // Upload the file (compressed or original)
  return api.uploadFile({ file: fileToUpload as unknown as Blob })
}

/**
 * Validates if a file is a valid image and within size limits
 * @param file - File to validate
 * @param maxSizeMB - Maximum file size in MB (default: 10MB)
 * @returns Validation result
 */
export const validateImageFile = (file: File, maxSizeMB = 10) => {
  const errors: string[] = []
  
  if (!file.type.startsWith('image/')) {
    errors.push('Please select an image file')
  }
  
  const maxSizeBytes = maxSizeMB * 1024 * 1024
  if (file.size > maxSizeBytes) {
    errors.push(`Image size must be less than ${maxSizeMB}MB`)
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
} 